import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { AgendaTabs } from '../shell/AgendaTabs';

export type Camp = { id: string; name: string; description: string | null; startsOn: string; endsOn: string; placeName: string | null; address: string | null; mapsUrl: string | null; priceCents: number; capacity: number | null; registrationOpen: boolean };
export const money = (cents: number) => (cents / 100).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
export const dateRange = (a: string, b: string) => {
  const f = (s: string) => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y!, m! - 1, d!).toLocaleDateString('es', { day: 'numeric', month: 'long' });
  };
  return a === b ? f(a) : `${f(a)} al ${f(b)}`;
};

/** Campamentos (uno por año o temporada). */
export function CampsPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const navigate = useNavigate();
  const data = useAsync((signal) => (churchId ? api.get<{ canCreate: boolean; items: (Camp & { enrolled: number })[] }>(`/calendar/churches/${churchId}/camps`, signal) : Promise.resolve(null)), [churchId]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;

  async function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const body = readCamp(new FormData(e.currentTarget));
    setBusy(true);
    setError(null);
    try {
      const { id } = await api.post<{ id: string }>(`/calendar/churches/${churchId}/camps`, body);
      navigate(`/app/campamentos/${id}`);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <AgendaTabs />
      <header className="page__header page__header--actions">
        <div>
          <h1>Campamentos</h1>
          <p className="muted">Inscripciones, pagos, transporte, carpas, cocina y más de cada campamento.</p>
        </div>
        {data.status === 'ready' && data.data?.canCreate && !creating ? <Button onClick={() => setCreating(true)}>Nuevo campamento</Button> : null}
      </header>
      {creating ? (
        <Card title="Nuevo campamento">
          <form className="form" onSubmit={create} noValidate>
            {error ? <Alert>{error}</Alert> : null}
            <CampFields />
            <div className="actions">
              <Button type="submit" busy={busy}>
                Crear
              </Button>
              <Button type="button" variant="ghost" onClick={() => setCreating(false)}>
                Cancelar
              </Button>
            </div>
          </form>
        </Card>
      ) : null}
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' && data.data && data.data.items.length === 0 ? <Card><EmptyState title="Todavía no hay campamentos." /></Card> : null}
      <ul className="ministry-grid">
        {(data.status === 'ready' && data.data ? data.data.items : []).map((c) => (
          <li key={c.id}>
            <Link to={`/app/campamentos/${c.id}`} className="ministry-card">
              <strong>{c.name}</strong>
              <span className="small">{dateRange(c.startsOn, c.endsOn)}{c.placeName ? ` · ${c.placeName}` : ''}</span>
              <span className="small muted">
                {c.enrolled} inscritos{c.capacity ? ` de ${c.capacity}` : ''} · {c.priceCents ? money(c.priceCents) : 'Sin costo'}
                {c.registrationOpen ? '' : ' · Inscripciones cerradas'}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function readCamp(f: FormData) {
  const t = (k: string) => String(f.get(k) ?? '').trim() || null;
  return {
    name: t('name') ?? '', description: t('description'), startsOn: t('startsOn') ?? '', endsOn: t('endsOn') ?? '', placeName: t('placeName'), address: t('address'), mapsUrl: t('mapsUrl'),
    priceCents: Math.round(Number(t('price') ?? 0) * 100), capacity: t('capacity') ? Number(t('capacity')) : null, registrationOpen: f.get('registrationOpen') === 'on',
  };
}

export function CampFields({ camp }: { camp?: Camp }) {
  return (
    <>
      <Field label="Nombre" name="name" required maxLength={120} defaultValue={camp?.name} placeholder="Campamento 2027" />
      <div className="form__row">
        <Field label="Salida" name="startsOn" type="date" required defaultValue={camp?.startsOn} />
        <Field label="Regreso" name="endsOn" type="date" required defaultValue={camp?.endsOn} />
      </div>
      <div className="form__row">
        <Field label="Lugar" name="placeName" maxLength={200} defaultValue={camp?.placeName ?? ''} />
        <Field label="Ubicación en el mapa" name="mapsUrl" type="url" maxLength={500} defaultValue={camp?.mapsUrl ?? ''} />
      </div>
      <Field label="Dirección" name="address" maxLength={300} defaultValue={camp?.address ?? ''} />
      <div className="form__row">
        <Field label="Costo por persona (MXN)" name="price" type="number" min={0} step="0.01" defaultValue={camp ? camp.priceCents / 100 : 0} />
        <Field label="Cupo (opcional)" name="capacity" type="number" min={1} defaultValue={camp?.capacity ?? ''} />
      </div>
      <div className="field">
        <label htmlFor="camp-desc">Descripción (opcional)</label>
        <textarea id="camp-desc" name="description" rows={3} maxLength={4000} defaultValue={camp?.description ?? ''} />
      </div>
      <label className="check">
        <input type="checkbox" name="registrationOpen" defaultChecked={camp?.registrationOpen ?? true} /> Inscripciones abiertas
      </label>
    </>
  );
}
